import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import type { TextFieldProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ActionButtonProps } from 'src/core/ui/CustomButton';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { classifyScan } from './scanClassifier';
import type { ScanClassification, ScanClassifierConfig } from './scanClassifier';

export const useScanner = (config: ScannerConfig) => {
  const [value, setInputValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef('');
  const renderedValueRef = useRef(value);
  const submittingRef = useRef(false);
  const mountedRef = useRef(false);
  const configRef = useRef(config);
  const focusStateRef = useRef<readonly unknown[] | null>(null);
  const { updateMessageStatus } = useMessageContext();
  configRef.current = config;
  renderedValueRef.current = value;
  const disabled = Boolean(config.disabled || config.busy || submitting);

  const setValue = useCallback((next: string) => { valueRef.current = next; setInputValue(next); }, []);
  const clear = useCallback(() => setValue(''), [setValue]);
  const focus = useCallback(() => {
    const current = configRef.current;
    if (current.disabled || current.busy || submittingRef.current) return;
    inputRef.current?.focus();
  }, []);
  const restoreFocus = useCallback(() => {
    const active = document.activeElement;
    if (active !== inputRef.current && active instanceof HTMLElement && (active.matches('input, textarea, select, [contenteditable="true"]') || active.closest('[role="dialog"], [role="menu"], [role="listbox"]'))) return;
    if (configRef.current.focus?.canRestore?.() === false) return;
    focus();
  }, [focus]);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => {
    const previous = focusStateRef.current;
    const state = [disabled, ...(config.focus?.restoreOn ?? [])];
    const changed = !previous || state.length !== previous.length || state.some((item, index) => !Object.is(item, previous[index]));
    focusStateRef.current = state;
    if (!changed || disabled) return;
    if (previous ? config.focus?.restore : config.focus?.autoFocus) restoreFocus();
  });

  const submit = useCallback((raw = valueRef.current, source: ScanSource = 'external'): Promise<boolean> => {
    const current = configRef.current;
    if (!mountedRef.current || current.disabled || current.busy || submittingRef.current) return Promise.resolve(false);
    submittingRef.current = true;
    const failed = (error: unknown) => {
      if (!mountedRef.current) return;
      if (current.onError) current.onError(error);
      else updateMessageStatus({ requestUrl: `/scanning/${current.id}`, status: 'error', message: error instanceof Error ? error.message : 'Could not process the scan.' });
    };
    const finish = () => {
      submittingRef.current = false;
      if (!mountedRef.current) return;
      setSubmitting(false);
      if (current.focus?.restore) restoreFocus();
    };
    try {
      const classification = classifyScan(raw, current.classification);
      if (!classification.value) { finish(); return Promise.resolve(false); }
      if (current.clearOnSubmit !== false) clear();
      const result = current.onScan({ ...classification, source });
      if (result && typeof result.then === 'function') {
        setSubmitting(true);
        return Promise.resolve(result).then(() => true, error => { failed(error); return false; }).finally(finish);
      }
      finish();
      return Promise.resolve(true);
    } catch (error) {
      try { failed(error); } finally { finish(); }
      return Promise.resolve(false);
    }
  }, [clear, restoreFocus, updateMessageStatus]);

  // React may restore the previous controlled DOM value between change and Enter in one batch.
  const submitFromInput = useCallback((source: 'keyboard' | 'button') => {
    const raw = valueRef.current !== renderedValueRef.current ? valueRef.current : inputRef.current?.value ?? valueRef.current;
    return submit(raw, source);
  }, [submit]);
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value), [setValue]);
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat) return;
    void submitFromInput('keyboard');
  }, [submitFromInput]);

  return { config, value, inputRef, disabled, submitting, setValue, clear, focus, submit, submitFromInput, onChange, onKeyDown };
};

export type ScanSource = 'keyboard' | 'button' | 'external';
export type ScannerEvent = ScanClassification & { source: ScanSource };
export type ScannerConfig = {
  id: string;
  onScan: (scan: ScannerEvent) => void | Promise<void>;
  disabled?: boolean;
  busy?: boolean;
  clearOnSubmit?: boolean;
  classification?: ScanClassifierConfig;
  input?: Pick<TextFieldProps, 'label' | 'placeholder' | 'size' | 'autoComplete' | 'sx'> & { ariaLabel?: string };
  submitButton?: Pick<ActionButtonProps, 'title' | 'tooltipText' | 'variant' | 'iconButton' | 'icon' | 'size'> & { visible?: boolean };
  focus?: { autoFocus?: boolean; restore?: boolean; restoreOn?: readonly unknown[]; canRestore?: () => boolean };
  sx?: SxProps<Theme>;
  onError?: (error: unknown) => void;
};
export type ScannerController = ReturnType<typeof useScanner>;

export default useScanner;
